import {defineStore} from 'pinia';
import {computed, ref} from 'vue';

import http from '@/shared/api/http';
import {useFaviconInjection} from '@/shared/composables/useFaviconInjection';
import type {FooterPages} from '@/shared/features/pages/page.model';
import type {SsoLoginButton} from '@/shared/features/ssoConnections/ssoConnection.model';
import type {LoginMode, TenantPublic} from '@/shared/features/tenants/tenant.model';

const emptyFooterPages = (): FooterPages => ({global: [], tenant: []});

export const useTenantConfigStore = defineStore('tenantConfig', () => {
   const tenantId = ref<number | null>(null);
   const name = ref<string | null>(null);
   const loginMode = ref<LoginMode>('k12');
   /**
    * The sign-in buttons this tenant has configured, in the order its admin set.
    *
    * Replaces the old single `classlink_enabled` flag: a tenant can offer several providers, and
    * which ones is a per-connection decision rather than one boolean about ClassLink.
    *
    * One list, not two. There used to be a second filtered by a per-connection "also show on the
    * student login page" switch, and hiding a button restricted nobody — a student reaching the
    * staff page signed in perfectly well, because nothing about a button or a page checks a role.
    * Who may sign in is settled by the roster and by the connection's allowed roles. The student
    * login page simply offers none of these; it takes the MDR, number and password a school
    * issues.
    */
   const ssoButtons = ref<SsoLoginButton[]>([]);
   const logoUrl          = ref<string | null>(null);
   const loginPageLogoUrl = ref<string | null>(null);
   const loginPageBgUrl   = ref<string | null>(null);
   /** Footer policy links (global line + tenant line), rendered by FooterLinks.vue. */
   const footerPages = ref<FooterPages>(emptyFooterPages());
   const resolved = ref<boolean>(false);

   /** Whether the current tenant is Workforce-mode. */
   const isWorkforce = computed(() => loginMode.value === 'workforce');

   /** Whether the current tenant is K12-mode (the default). */
   const isK12 = computed(() => loginMode.value !== 'workforce');

   /**
    * Fetch public tenant config from the backend and populate the store.
    * Called once on app boot before mount; call again after a page is
    * created, edited or deleted so the footer links refresh without a reload.
    */
   async function resolveTenant(): Promise<void> {
      try {
         const res = await http.get('/tenant/resolve') as TenantPublic;
         tenantId.value         = res.id ?? null;
         name.value             = res.name ?? null;
         loginMode.value        = res.login_mode ?? 'k12';
         ssoButtons.value       = res.sso_buttons ?? [];
         logoUrl.value          = res.logo_url          ?? null;
         loginPageLogoUrl.value = res.login_page_logo_url ?? null;
         loginPageBgUrl.value   = res.login_page_bg_url   ?? null;
         footerPages.value      = res.footer_pages ?? emptyFooterPages();
         useFaviconInjection(res);
      } catch {
         // non-fatal — keep defaults
      } finally {
         resolved.value = true;
      }
   }

   return {tenantId, name, loginMode, isWorkforce, isK12, ssoButtons, logoUrl, loginPageLogoUrl, loginPageBgUrl, footerPages, resolved, resolveTenant};
});
